import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileCode2, FileMinus2, Printer } from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { VistaPreviaDeImpresion } from '@/shared/ui/VistaPreviaDeImpresion';
import { notificar } from '@/shared/ui/avisos';
import { colorDeLaSituacion } from './estadosDeFactura';

const SITUACIONES = [
  { valor: '', etiqueta: 'Todas' },
  { valor: 'autorizada', etiqueta: 'Autorizadas' },
  { valor: 'enviada', etiqueta: 'Enviadas' },
  { valor: 'firmada', etiqueta: 'Sin respuesta' },
  { valor: 'rechazada', etiqueta: 'Rechazadas' },
] as const;

interface Nota {
  id: number;
  facturaId: number;
  numeroDeLaFactura: string | null;
  cliente: string;
  identificacion: string;
  tipo: string;
  motivo: string;
  observacion: string | null;
  total: number;
  numero: string | null;
  claveAcceso: string | null;
  situacionSri: string;
  numeroAutorizacion: string | null;
  fechaAutorizacion: string | null;
  mensajeSri: string | null;
  fechaEmision: string;
  solicitadoPor: string;
  aprobadoPor: string | null;
  tieneXml: boolean;
  /** Las que vinieron del sistema anterior: se consultan, no se operan. */
  historica: boolean;
}

export function PaginaNotasDeCredito() {
  const [buscar, setBuscar] = useState('');
  const [situacionSri, setSituacion] = useState('');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [bajando, setBajando] = useState<number | null>(null);
  const [ride, setRide] = useState<Blob | null>(null);

  const notas = useQuery({
    queryKey: ['notas-credito', buscar, situacionSri, pagina, tamanio],
    queryFn: () =>
      api.get<Pagina<Nota>>('/notas-credito', {
        query: { buscar, situacionSri, pagina, tamanio },
      }),
  });

  async function abrirRide(nota: Nota) {
    setBajando(nota.id);
    setRide(null);
    try {
      const fichero = await api.descargar(`/notas-credito/${nota.id}/ride.pdf`);
      if (!fichero) {
        notificar.error('NO SE PUDO GENERAR EL RIDE');
        return;
      }
      setRide(fichero);
    } catch (causa) {
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo abrir el RIDE');
    } finally {
      setBajando(null);
    }
  }

  async function descargarXml(nota: Nota) {
    setBajando(nota.id);
    try {
      const fichero = await api.descargar(`/notas-credito/${nota.id}/comprobante.xml`);
      if (!fichero) {
        notificar.error('ESA NOTA TODAVÍA NO TIENE COMPROBANTE');
        return;
      }

      const url = URL.createObjectURL(fichero);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = `${nota.numero ?? nota.id}.xml`;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      URL.revokeObjectURL(url);
    } catch (causa) {
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo descargar');
    } finally {
      setBajando(null);
    }
  }

  const columnas: Columna<Nota>[] = [
    {
      clave: 'numero',
      encabezado: 'Nº nota',
      contenido: (n) => (
        <span className="block">
          <span className="block font-mono text-xs font-semibold text-armazon-700">
            {n.numero ?? <span className="text-armazon-400">SIN EMITIR</span>}
          </span>
          <span className="block text-[10px] uppercase text-armazon-400">
            {n.tipo === 'total' ? 'Total' : 'Parcial'}
          </span>
          {/* Hay que poder distinguirlas de un vistazo: estas no tienen XML que bajar ni
              trámite que seguir, y preguntarse por qué es lo primero que pasa al verlas. */}
          {n.historica && (
            <span className="mt-0.5 inline-block rounded bg-armazon-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-armazon-500">
              Sistema anterior
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'cliente',
      encabezado: 'Cliente',
      contenido: (n) => (
        <span className="block">
          <span className="block truncate font-medium text-armazon-700">{n.cliente}</span>
          <span className="block font-mono text-[10px] text-armazon-400">{n.identificacion}</span>
        </span>
      ),
    },
    {
      clave: 'acredita',
      encabezado: 'Acredita a',
      contenido: (n) => (
        <span className="font-mono text-xs text-armazon-600">{n.numeroDeLaFactura}</span>
      ),
    },
    {
      clave: 'motivo',
      encabezado: 'Motivo',

      contenido: (n) => (
        <span className="block max-w-sm">
          {n.historica ? (
            <span className="mb-1 inline-block rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
              {n.motivo}
            </span>
          ) : (
            <span className="block text-[11px] leading-relaxed text-armazon-600">{n.motivo}</span>
          )}
          {n.observacion && (
            <span className="block text-[10px] leading-relaxed text-armazon-400">
              {n.observacion}
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'total',
      encabezado: 'Acreditado',
      alinear: 'derecha',
      contenido: (n) => <span className="importe">${n.total.toFixed(2)}</span>,
    },
    {
      clave: 'situacion',
      encabezado: 'Ante el SRI',
      alinear: 'centro',
      contenido: (n) => (
        <span className="block">
          <span
            className={`block text-[11px] font-semibold uppercase ${colorDeLaSituacion(n.situacionSri)}`}
          >
            {n.situacionSri}
          </span>
          {n.situacionSri === 'rechazada' && n.mensajeSri && (
            <span className="mt-0.5 block max-w-xs text-[10px] leading-relaxed text-red-700">
              {n.mensajeSri}
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'quien',
      encabezado: 'Quién',
      contenido: (n) => (
        <span className="block text-[10px] uppercase text-armazon-500">
          <span className="block">Pide: {n.solicitadoPor}</span>
          {n.aprobadoPor && <span className="block">Autoriza: {n.aprobadoPor}</span>}
        </span>
      ),
    },
    {
      clave: 'acciones',
      encabezado: '',
      alinear: 'derecha',
      contenido: (n) =>
        n.tieneXml ? (
          <span className="flex justify-end gap-1">
            <BotonDeFila
              icono={Printer}
              titulo="Abrir el RIDE"
              alPulsar={() => void abrirRide(n)}
              ocupado={bajando === n.id}
            />
            <BotonDeFila
              icono={FileCode2}
              titulo="Descargar el XML autorizado"
              alPulsar={() => void descargarXml(n)}
              ocupado={bajando === n.id}
            />
          </span>
        ) : null,
    },
  ];

  return (
    <>
      <EncabezadoDePagina
        icono={FileMinus2}
        color="ambar"
        titulo="Notas de crédito"
        subtitulo="Facturas dejadas sin efecto ante el SRI, y por qué"
      />

      <Tabla
        datos={notas.data}
        cargando={notas.isLoading}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(nuevo) => {
          setTamanio(nuevo);
          setPagina(0);
        }}
        iconoVacio={FileMinus2}
        mensajeVacio="Todavía no se ha acreditado ninguna factura"
        entidad="NOTAS"
        herramientas={
          <span className="flex flex-wrap items-center gap-2">
            <select
              value={situacionSri}
              onChange={(evento) => {
                setSituacion(evento.target.value);
                setPagina(0);
              }}
              aria-label="Situación ante el SRI"
              className="h-9 rounded-lg border border-armazon-200 bg-white px-2 text-xs uppercase text-armazon-700 outline-none focus:border-marca-400"
            >
              {SITUACIONES.map((s) => (
                <option key={s.valor} value={s.valor}>
                  {s.etiqueta}
                </option>
              ))}
            </select>

            <CampoDeBusqueda
              valor={buscar}
              alCambiar={(texto) => {
                setBuscar(texto);
                setPagina(0);
              }}
              marcador="Nombre, cédula, nº de nota o de factura"
            />
          </span>
        }
      />

      <VistaPreviaDeImpresion
        abierto={ride !== null || bajando !== null}
        alCerrar={() => setRide(null)}
        titulo="RIDE de la nota de crédito"
        subtitulo="Representación impresa del comprobante electrónico"
        fichero={ride}
        cargando={bajando !== null}
        formato="hoja"
        textoDeCarga="Preparando el RIDE de la nota…"
      />
    </>
  );
}
